import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import type {ReviewSummaryState, ConversationReviewSummary} from '../../page-data/payloads/conversation-comments'
import {useReactToReviewSummaryMutation} from '../../mutations/use-react-to-review-summary-mutation'
import {ReviewApprovedIcon} from '@github-ui/custom-icons/ReviewApprovedIcon'
import {ReviewChangesRequestedIcon} from '@github-ui/custom-icons/ReviewChangesRequestedIcon'
import {ReviewCommentedIcon} from '@github-ui/custom-icons/ReviewCommentedIcon'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {useRef, useMemo, useState, use} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {StatusAvatar} from '@github-ui/status-avatar/StatusAvatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link, Button} from '@primer/react'
import {HistoryIcon} from '@primer/octicons-react'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {OnBehalfOfTeams} from '../overview/reviewers/OnBehalfOfTeams'
import {legacyReviewerToProto} from '../../page-data/payloads/review-requests'
import {clsx} from 'clsx'
import {ThreadPreview} from './ThreadPreview'
import {CommentLayout} from './CommentLayout'
import {ConversationItemActions, ConversationItemActionsProvider} from './ConversationItemActions'
import {reviewSummaryAnchor} from './utils/comment-anchors'
import {CommentsContext} from './CommentsContext'
import {useFocusHashTarget} from '../../hooks/use-focus-hash-target'
import styles from './ReviewSummaryItem.module.css'
import {announce} from '@github-ui/aria-live'
import {ConversationItemBody} from './ConversationItemBody'
import {useBodyEditManager} from './use-body-edit-manager'
import {useUpdateReviewSummaryMutation} from '../../mutations/use-update-review-summary-mutation'
import {useHideReviewSummaryMutation} from '../../mutations/use-hide-review-summary-mutation'
import {useUnhideReviewSummaryMutation} from '../../mutations/use-unhide-review-summary-mutation'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ConversationItemFallback} from './ConversationItemFallback'
import {useReturnFocus} from '@github-ui/use-return-focus'
import type {Icon} from '@primer/octicons-react'
import {reviewStateLabel} from '../../helpers/review-state-labels'
import {ReviewState} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {isOwnedThread} from './utils/is-owned-thread'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {CommentsSkeleton} from './CommentsSkeleton'

const REVIEW_TOGGLE_MINIMIZE_LABELS = {expand: 'Expand review', collapse: 'Collapse review'}

interface ReviewSummaryItemProps {
  reviewSummary: ConversationReviewSummary
  pullRequestAuthorLogin: string
  // Optional flag to show the review state icon next to the review summary. Defaults to false.
  withReviewStateIcon?: boolean
  // Optional callback for handling quote reply actions. If provided, a "Quote reply" button will be rendered.
  onQuoteReply?: (quotedText: string) => void
  className?: string
  onNavigateToDiffComment?: () => void
  threads?: Thread[]
  isLoadingThreads?: boolean
}

function getReviewStateVisual(
  state: ReviewSummaryState,
  dismissedState?: ReviewSummaryState | null,
): {icon: Icon; className: string} {
  switch (state) {
    case 'APPROVED':
      return {
        icon: ReviewApprovedIcon,
        className: styles.approvedStatus,
      }
    case 'CHANGES_REQUESTED':
      return {
        icon: ReviewChangesRequestedIcon,
        className: styles.changesRequestedStatus,
      }
    case 'COMMENTED':
      return {
        icon: ReviewCommentedIcon,
        className: styles.commentedStatus,
      }
    case 'DISMISSED': {
      const icon = dismissedState ? getReviewStateVisual(dismissedState).icon : ReviewCommentedIcon
      return {icon, className: styles.dismissedStatus}
    }
  }
}

const emptyThreads: Thread[] = []

function UnsafeReviewSummaryItem({
  reviewSummary,
  pullRequestAuthorLogin,
  onQuoteReply: externalOnQuoteReply,
  withReviewStateIcon,
  className,
  onNavigateToDiffComment,
  id,
  threads = emptyThreads,
  isLoadingThreads = false,
}: ReviewSummaryItemProps & {id: string}) {
  const commentRef = useRef<HTMLElement>(null)
  const canComment = useLayoutCanComment()
  const {helpUrl} = useLayoutRouteData()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const reactToReviewSummaryMutation = useReactToReviewSummaryMutation()

  const hasBody = reviewSummary.body.trim().length > 0

  const onQuoteReply = useMemo(() => {
    if (!canComment || !externalOnQuoteReply || !hasBody) return undefined

    return () => {
      const current = ssrSafeWindow?.getSelection()
      let currentSelection: SelectionContext | null = null

      if (current && current.anchorNode && current.rangeCount > 0) {
        currentSelection = {
          anchorNode: current.anchorNode,
          range: current.getRangeAt(0),
        }
      }

      const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, reviewSummary.body)
      externalOnQuoteReply(quotedText ?? reviewSummary.body)
    }
  }, [canComment, externalOnQuoteReply, hasBody, reviewSummary.body])
  const reviewStateVisual = withReviewStateIcon
    ? getReviewStateVisual(reviewSummary.state, reviewSummary.dismissedState)
    : null
  const isHidden = reviewSummary.isHidden
  // minimized state is tracked for when a user manually expands the body of a hidden comment.
  const [isMinimized, setIsMinimized] = useState(isHidden)

  const hasLoadingThreads = isLoadingThreads && reviewSummary.reviewThreadIds.length > 0
  const hasThreads = hasLoadingThreads || threads.length > 0
  const reviewSummaryId = reviewSummary.id
  const showHiddenNotice = hasBody && isHidden && reviewSummary.viewerCanSeeUnminimizeButton

  const threadsElement = (
    <>
      {hasLoadingThreads ? (
        <CommentsSkeleton count={reviewSummary.reviewThreadIds.length} reviewCommentsMode />
      ) : (
        threads.map(thread => (
          <ThreadPreview
            key={thread.id}
            thread={thread}
            selectedReviewId={reviewSummaryId}
            onNavigateToDiffComment={onNavigateToDiffComment}
            headingLevel="h2"
          />
        ))
      )}
    </>
  )

  const editBodyMutation = useUpdateReviewSummaryMutation({
    onSuccess: () => announce('Review updated successfully.'),
  })
  const directEditBodyMutation = useUpdateReviewSummaryMutation()
  const editManager = useBodyEditManager(reviewSummary, editBodyMutation, directEditBodyMutation)

  const hideMutation = useHideReviewSummaryMutation()
  const unhideMutation = useUnhideReviewSummaryMutation()

  const {containerRef: editorContainerRef} = useReturnFocus(commentRef)

  const isOpinionless = reviewSummary.state === 'COMMENTED'

  // If there's only one thread and the review is COMMENTED without a body, depending on whether the thread belongs to
  // this review, render just the thread in a container for review navigation. Otherwise, render nothing. Hidden
  // reviews skip this shortcut so they still render collapsed behind the fold/unfold toggle.
  if (!isHidden && threads.length === 1 && isOpinionless && !hasBody) {
    if (threads[0] && isOwnedThread(reviewSummaryId, threads[0])) {
      return <div id={id}>{threadsElement}</div>
    } else {
      return null
    }
  }

  if (!reviewSummary.repository) return null

  return (
    <ConversationItemActionsProvider onQuoteReply={onQuoteReply}>
      <CommentLayout
        ref={commentRef}
        className={clsx('border rounded-2', className)}
        tabIndex={-1}
        id={id}
        header={
          <>
            <ActivityHeader
              avatarUrl={reviewSummary.author?.avatarUrl || ''}
              userAvatar={
                reviewStateVisual ? (
                  <Link
                    href={`/${reviewSummary.author?.login ?? ''}`}
                    aria-label={`@${reviewSummary.author?.login ?? ''}'s profile`}
                    className={clsx(isHidden && styles.hiddenAvatar)}
                    {...getHovercardAttributesForType('user', {login: reviewSummary.author?.login})}
                  >
                    <StatusAvatar
                      altText={`@${reviewSummary.author?.login ?? ''}`}
                      src={reviewSummary.author?.avatarUrl ?? ''}
                      square={false}
                      icon={reviewStateVisual.icon}
                      iconColor="var(--review-state-icon-color)"
                      backgroundColor="var(--review-state-background-color)"
                      className={reviewStateVisual.className}
                      backgroundClassName={styles.statusBackground}
                      size={24}
                    />
                  </Link>
                ) : undefined
              }
              comment={{
                id: String(reviewSummary.id),
                authorAssociation: reviewSummary.authorAssociation,
                createdAt: reviewSummary.createdAt,
                isHidden: reviewSummary.isHidden,
                minimizedReason: reviewSummary.minimizedReason,
                repository: reviewSummary.repository,
                url: reviewSummary.url,
              }}
              commentAuthorLogin={reviewSummary.author?.login ?? ''}
              commentSubjectType="pull request"
              commentSubjectAuthorLogin={pullRequestAuthorLogin}
              forceInlineAvatar
              isMinimized={isMinimized}
              noContainerStyles
              tooltipDirection="sw"
              additionalHeaderMessage={
                <>
                  {reviewStateLabel(
                    ReviewState[reviewSummary.state],
                    reviewSummary.dismissedState ? ReviewState[reviewSummary.dismissedState] : undefined,
                  )}
                  &nbsp;
                </>
              }
              actions={
                editManager.isEditing ? (
                  <></>
                ) : (
                  <ConversationItemActions
                    authorLogin={reviewSummary.author?.login ?? ''}
                    contentId={reviewSummary.relayId}
                    contentUrl={reviewSummary.url}
                    link={reviewSummary.url}
                    navigationSource="pull_request_review"
                    onQuoteReply={onQuoteReply}
                    onEdit={hasBody ? editManager.startEditIfAllowed : undefined}
                    isHidden={isHidden}
                    isMinimized={isMinimized}
                    onHide={
                      reviewSummary.viewerCanSeeMinimizeButton
                        ? reason => {
                            setIsMinimized(true)
                            hideMutation.mutate({id: reviewSummary.id, reason}, {onError: () => setIsMinimized(false)})
                          }
                        : undefined
                    }
                    onUnhide={
                      reviewSummary.viewerCanSeeUnminimizeButton
                        ? () => {
                            setIsMinimized(false)
                            unhideMutation.mutate({id: reviewSummary.id}, {onError: () => setIsMinimized(true)})
                          }
                        : undefined
                    }
                    onToggleMinimize={() => setIsMinimized(prev => !prev)}
                    toggleMinimizeLabels={REVIEW_TOGGLE_MINIMIZE_LABELS}
                    githubHelpUrl={helpUrl}
                    organizationLogin={reviewSummary.repository?.owner.login}
                    viewerCanBlockFromOrg={reviewSummary.viewerCanBlockFromOrg}
                    viewerCanBlockPersonally={reviewSummary.viewerCanBlockPersonally}
                    viewerCanUnblockFromOrg={reviewSummary.viewerCanUnblockFromOrg}
                    viewerCanUnblockPersonally={reviewSummary.viewerCanUnblockPersonally}
                  />
                )
              }
            />
            <OnBehalfOfTeams
              teams={reviewSummary.onBehalfOfTeams.map(legacyReviewerToProto)}
              className="pt-1"
              withAvatar={false}
            />
          </>
        }
      >
        {(hasBody || hasThreads) && !isMinimized && (
          <div className="d-flex flex-column position-relative width-full gap-3">
            {hasBody && !isMinimized && (
              <ConversationItemBody
                item={reviewSummary}
                reactMutation={reactToReviewSummaryMutation}
                editManager={editManager}
                editorContainerRef={editorContainerRef}
              />
            )}
            {showHiddenNotice && (
              <div className={styles.hiddenNotice}>
                <Button
                  block
                  variant="invisible"
                  size="small"
                  onClick={() =>
                    unhideMutation.mutate(
                      {id: reviewSummary.id},
                      {
                        onSuccess: () => {
                          setIsMinimized(false)
                          // Lint warning is false alarm; the read is deferred by the settimeout
                          // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-input-handler
                          setTimeout(() => commentRef.current?.focus(), 0)
                        },
                      },
                    )
                  }
                >
                  <span className={styles.unhideLabel}>
                    <HistoryIcon className={styles.unhideIcon} />
                    Unhide
                  </span>
                </Button>
              </div>
            )}
            {hasThreads && !isMinimized && threadsElement}
          </div>
        )}
      </CommentLayout>
    </ConversationItemActionsProvider>
  )
}

export function ReviewSummaryItem({reviewSummary, className, ...props}: ReviewSummaryItemProps) {
  const anchorPrefix = use(CommentsContext).commentAnchorPrefix
  const id = reviewSummaryAnchor(String(reviewSummary.id), anchorPrefix)
  useFocusHashTarget(id)

  return (
    <ErrorBoundary
      fallback={<ConversationItemFallback tabIndex={-1} id={id} item={reviewSummary} className={className} />}
      boundaryName="IssueCommentItem"
    >
      <UnsafeReviewSummaryItem id={id} reviewSummary={reviewSummary} className={className} {...props} />
    </ErrorBoundary>
  )
}
